import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Sprout, Check, Loader2, AlertCircle, Send, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AiutoPuliziaPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    terrain_size: "",
    description: "",
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const onField = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setStatus("loading");
    try {
      await axios.post(`${API}/help-requests`, {
        ...form,
        terrain_size: form.terrain_size || null,
      });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setError(err?.response?.data?.detail || "Errore. Riprova più tardi.");
    }
  };

  return (
    <Layout>
      <section data-testid="aiuto-pulizia-header" className="bg-[#1f2d22] text-white py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-4 py-1.5 mb-5">
            <Sprout className="w-3.5 h-3.5 text-[#E8C9A0]" />
            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.22em] text-[#E8C9A0]">
              Cura del territorio
            </span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight">
            Richiedi aiuto per la <span className="italic font-light text-[#E8C9A0]">pulizia del tuo terreno.</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-white/75 max-w-2xl leading-relaxed">
            Hai un terreno colpito dall'incendio o necessita di cura? Compila il modulo
            e ti contatteremo per organizzare insieme l'intervento.
          </p>
        </div>
      </section>

      <section className="py-14 sm:py-20 bg-[#FBF9F6]">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12">
          {status === "success" ? (
            <div data-testid="aiuto-pulizia-success" className="rounded-3xl bg-[#2E4F3B] text-white p-10 sm:p-14 text-center">
              <div className="w-16 h-16 rounded-full bg-white/15 flex items-center justify-center mx-auto mb-6">
                <Check className="w-8 h-8 text-[#E8C9A0]" />
              </div>
              <h2 className="font-display text-3xl font-black">Richiesta ricevuta.</h2>
              <p className="mt-4 text-white/80 max-w-xl mx-auto leading-relaxed">
                Grazie! Abbiamo ricevuto la tua richiesta. Ti contatteremo a breve per organizzare insieme l'intervento.
              </p>
              <Link
                to="/progetti/cura-e-pulizia-del-territorio"
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-white text-[#1A1A1A] font-bold px-6 py-3 hover:bg-[#E8C9A0] transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Torna al progetto
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="aiuto-pulizia-form" className="bg-white border-2 border-[#E5E1D8] rounded-3xl p-7 sm:p-10 space-y-5">
              <Field name="name" label="Nome e cognome *" value={form.name} onChange={onField} required testid="help-name" />
              <div className="grid sm:grid-cols-2 gap-4">
                <Field name="email" type="email" label="Email *" value={form.email} onChange={onField} required testid="help-email" />
                <Field name="phone" type="tel" label="Telefono *" value={form.phone} onChange={onField} required testid="help-phone" />
              </div>
              <Field
                name="address"
                label="Indirizzo del terreno *"
                value={form.address}
                onChange={onField}
                required
                placeholder="Via, località, Asciano (PI)"
                testid="help-address"
              />
              <Field
                name="terrain_size"
                label="Dimensione approssimativa del terreno"
                value={form.terrain_size}
                onChange={onField}
                placeholder="es. 500 mq, 1 ettaro, 2 oliveti…"
                testid="help-size"
              />
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C5C5C] mb-2">
                  Descrizione della situazione *
                </label>
                <textarea
                  name="description"
                  rows={6}
                  required
                  minLength={5}
                  value={form.description}
                  onChange={onField}
                  data-testid="help-description"
                  placeholder="Descrivi lo stato del terreno, l'urgenza e di che tipo di aiuto avresti bisogno…"
                  className="w-full bg-[#FBF9F6] border-2 border-[#E5E1D8] rounded-xl px-4 py-3 text-[#1A1A1A] focus:outline-none focus:border-[#2E4F3B] focus:ring-2 focus:ring-[#2E4F3B]/10 transition-all resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={status === "loading"}
                data-testid="help-submit"
                className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-[#2E4F3B] hover:bg-[#1f3828] text-white font-bold text-base px-8 py-4 transition-all disabled:opacity-60 shadow-lg"
              >
                {status === "loading" ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Invio in corso…
                  </>
                ) : (
                  <>
                    Invia la richiesta
                    <Send className="w-4 h-4" />
                  </>
                )}
              </button>

              {error && (
                <div className="flex items-start gap-3 bg-[#C15C3D]/10 border border-[#C15C3D]/30 rounded-xl p-3">
                  <AlertCircle className="w-4 h-4 text-[#C15C3D] shrink-0 mt-0.5" />
                  <p className="text-xs text-[#1A1A1A]">{error}</p>
                </div>
              )}

              <p className="text-[11px] text-[#5C5C5C] text-center leading-relaxed">
                I tuoi dati saranno usati esclusivamente per organizzare l'intervento di pulizia.
              </p>
            </form>
          )}
        </div>
      </section>
    </Layout>
  );
}

const Field = ({ name, label, value, onChange, type = "text", required, placeholder, testid }) => (
  <div>
    <label htmlFor={name} className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C5C5C] mb-2">
      {label}
    </label>
    <input
      id={name}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full bg-[#FBF9F6] border-2 border-[#E5E1D8] rounded-xl px-4 py-3 text-[#1A1A1A] focus:outline-none focus:border-[#2E4F3B] focus:ring-2 focus:ring-[#2E4F3B]/10 transition-all"
    />
  </div>
);
